import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import type {
  ComposerSummary,
  NameI18n,
  ResourceState,
} from '@displaycms/contracts';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { pickName } from '@/lib/i18n-name';
import { Alert } from '@/components/ui/alert';
import { useAuth } from '@/auth/auth-context';
import {
  listComposers,
  importComposerJson,
  type ComposerListResponse,
} from '@/api/composers';
import { formatDateShort, type Locale } from '@/lib/format';

/**
 * `/composers` — paginated composers list (Task 27).
 *
 * Shows state badges, FR name, kind ("Boucle" for the only Phase-1 kind),
 * created_at. "Nouvelle programmation" button navigates to `/composers/new`.
 * Empty state in FR.
 */

const PAGE_SIZE = 20;

export function ComposersListPage(): JSX.Element {
  const { t, i18n } = useTranslation(['composers']);
  const { hasPermission } = useAuth();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const canCreate = hasPermission('composers.create');

  const [page, setPage] = useState<number>(1);
  const [importDialogOpen, setImportDialogOpen] = useState(false);

  const query = useQuery<ComposerListResponse>({
    queryKey: ['composers', 'list', page],
    queryFn: () => listComposers({ page, per_page: PAGE_SIZE }),
    staleTime: 30_000,
  });

  const items = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const lang: 'fr' | 'en' = i18n.language === 'en' ? 'en' : 'fr';

  return (
    <div className="flex flex-col gap-4" data-testid="composers-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">
            {t('composers:page_title')}
          </h1>
          <p className="text-sm text-muted-foreground">
            {t('composers:page_subtitle')}
          </p>
        </div>
        {canCreate && (
          <div className="flex items-center gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setImportDialogOpen(true)}
              data-testid="composers-import-btn"
            >
              {t('composers:import_button', { defaultValue: 'Importer JSON' })}
            </Button>
            <Button
              type="button"
              onClick={() => navigate('/composers/new')}
              data-testid="new-composer-button"
            >
              {t('composers:new_button')}
            </Button>
          </div>
        )}
      </header>

      {importDialogOpen && (
        <ImportJsonDialog
          onClose={() => setImportDialogOpen(false)}
          onImported={(id) => {
            setImportDialogOpen(false);
            void queryClient.invalidateQueries({ queryKey: ['composers', 'list'] });
            navigate(`/composers/${id}`);
          }}
        />
      )}

      {query.isLoading ? (
        <p
          role="status"
          data-testid="composers-loading"
          className="text-sm text-muted-foreground"
        >
          {t('composers:list_loading')}
        </p>
      ) : query.isError ? (
        <Alert variant="danger" data-testid="composers-error">
          {t('composers:list_error')}
        </Alert>
      ) : items.length === 0 ? (
        <p
          data-testid="composers-empty"
          className="rounded-md border border-dashed border-border p-6 text-center text-sm text-muted-foreground"
        >
          {t('composers:empty_state')}
        </p>
      ) : (
        <ComposersTable items={items} lang={lang} />
      )}

      <nav
        aria-label={t('common:pagination', { defaultValue: 'Pagination' })}
        className="flex items-center justify-between gap-2 text-sm text-muted-foreground"
      >
        <span data-testid="composers-page-summary">
          {t('composers:page_subtitle')}
        </span>
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page <= 1}
            data-testid="composers-page-prev"
          >
            {'←'}
          </Button>
          <span data-testid="composers-page-indicator">
            {page} / {totalPages}
          </span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setPage((p) => p + 1)}
            disabled={page >= totalPages || total === 0}
            data-testid="composers-page-next"
          >
            {'→'}
          </Button>
        </div>
      </nav>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Import JSON Dialog
// ---------------------------------------------------------------------------

function ImportJsonDialog({
  onClose,
  onImported,
}: {
  onClose: () => void;
  onImported: (composerId: string) => void;
}): JSX.Element {
  const { t } = useTranslation(['composers']);
  const fileRef = useRef<HTMLInputElement>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async () => {
    setError(null);
    const file = fileRef.current?.files?.[0];
    if (!file) {
      setError(
        t('composers:import.select_file', {
          defaultValue: 'Sélectionnez un fichier .json',
        }),
      );
      return;
    }
    setSubmitting(true);
    try {
      const text = await file.text();
      const parsed = JSON.parse(text) as unknown;
      const created = await importComposerJson(parsed);
      toast.success(
        t('composers:import.success', { defaultValue: 'Composer importé' }),
      );
      onImported(created.id);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : t('composers:import.generic_error', {
              defaultValue: 'Échec import',
            }),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent data-testid="composers-import-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('composers:import.title', { defaultValue: 'Importer un compositeur' })}
          </DialogTitle>
        </DialogHeader>
        <p className="mt-1 text-sm text-muted-foreground">
          {t('composers:import.description', {
            defaultValue:
              'Sélectionnez un fichier JSON exporté pour créer un nouveau compositeur.',
          })}
        </p>
        <input
          ref={fileRef}
          type="file"
          accept=".json,application/json"
          data-testid="composers-import-file"
          className="mt-4 block w-full text-sm"
        />
        {error && (
          <Alert variant="danger" className="mt-3" data-testid="composers-import-error">
            {error}
          </Alert>
        )}
        <div className="mt-6 flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={submitting}
            data-testid="composers-import-cancel"
          >
            {t('common:cancel', { defaultValue: 'Annuler' })}
          </Button>
          <Button
            type="button"
            onClick={handleSubmit}
            disabled={submitting}
            data-testid="composers-import-submit"
          >
            {submitting
              ? t('composers:import.submitting', { defaultValue: '…' })
              : t('composers:import.submit', { defaultValue: 'Importer' })}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Table
// ---------------------------------------------------------------------------

function ComposersTable({
  items,
  lang,
}: {
  items: ReadonlyArray<ComposerSummary>;
  lang: 'fr' | 'en';
}): JSX.Element {
  const { t } = useTranslation(['composers']);
  return (
    <div className="overflow-x-auto rounded-md border border-border">
      <table className="w-full text-sm" data-testid="composers-table">
        <thead className="bg-muted/40">
          <tr>
            <th className="px-3 py-2 text-left font-medium">
              {t('composers:column.name')}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {t('composers:column.kind')}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {t('composers:column.state')}
            </th>
            <th className="px-3 py-2 text-left font-medium">
              {t('composers:column.created_at')}
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <ComposerRow key={c.id} composer={c} lang={lang} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ComposerRow({
  composer,
  lang,
}: {
  composer: ComposerSummary;
  lang: 'fr' | 'en';
}): JSX.Element {
  const { t } = useTranslation(['composers']);
  const displayName = pickName(composer.name_i18n, lang);
  return (
    <tr
      data-testid={`composer-row-${composer.id}`}
      className="border-t border-border"
    >
      <td className="px-3 py-2">
        <Link
          to={`/composers/${composer.id}`}
          className="font-medium text-[hsl(var(--primary-text))] hover:underline"
        >
          {displayName}
        </Link>
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {t(`composers:kind.${composer.kind}`)}
      </td>
      <td className="px-3 py-2">
        <StateBadge state={composer.state} />
      </td>
      <td className="px-3 py-2 text-muted-foreground">
        {formatDate(composer.created_at, lang)}
      </td>
    </tr>
  );
}

function StateBadge({ state }: { state: ResourceState }): JSX.Element {
  const { t } = useTranslation(['composers']);
  const tone = STATE_TONE[state] ?? 'bg-muted text-muted-foreground';
  return (
    <span
      data-testid={`composer-state-badge-${state}`}
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${tone}`}
    >
      {t(`composers:state.${state}`)}
    </span>
  );
}

const STATE_TONE: Record<ResourceState, string> = {
  draft: 'bg-muted text-muted-foreground',
  pending_review: 'bg-amber-100 text-amber-900',
  recommended: 'bg-blue-100 text-blue-900',
  approved: 'bg-green-100 text-green-900',
  revoked: 'bg-red-100 text-red-900',
  expired: 'bg-zinc-200 text-zinc-700',
};


function formatDate(iso: string, lang: Locale): string {
  return formatDateShort(iso, lang);
}

export default ComposersListPage;
